import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NavbarDashbord } from './navbarDashbord';
import { updateClimatisseur } from './action';
import { Trash2, CheckCircle, MessageSquare, Plus, User, Heart, MessageCircle, X, ShoppingCart } from 'lucide-react';
import { SupprimerCommentaire, ModifierStatutCommentaire, AjouterCommentaireAdmin } from './slice';
import '../styles/admin.css';

export function PageCommentaires() {
    const listC = useSelector(state => state.listC) || [];
    const dispatch = useDispatch();
    const location = useLocation();
    
    // States
    const [allComments, setAllComments] = useState([]);
    const [highlightId, setHighlightId] = useState(null);
    const [filterProduct, setFilterProduct] = useState('Tous les produits');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newAdminComment, setNewAdminComment] = useState({ climatiseurId: '', username: 'Admin', avis: '5', commantaire: '' });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cid = params.get('climatiseurId');
        
        let flattened = listC.flatMap(c => 
            (c.commantaires || []).map(com => ({
                ...com,
                climatiseurId: c.id,
                climatiseurNom: c.nomClimatisseur
            }))
        );

        if (cid) {
            flattened = flattened.filter(com => com.climatiseurId == cid);
        }

        // Sort by ID descending (newest first)
        flattened.sort((a, b) => b.id - a.id);

        setAllComments(flattened);

        const highlight = params.get('highlight');
        if (highlight) {
            const hId = parseInt(highlight);
            setHighlightId(hId);
            setTimeout(() => {
                const el = document.getElementById(`comment-${hId}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
        }
    }, [listC, location]);

    const filteredComments = allComments.filter(com => {
        if (filterProduct !== 'Tous les produits' && com.climatiseurNom !== filterProduct) return false;
        return true;
    });

    const handleSupprimer = (climatiseurId, commentaireId) => {
        if (window.confirm("Voulez-vous vraiment supprimer ce commentaire ?")) {
            dispatch(SupprimerCommentaire({ climatiseurId, commentaireId }));
            
            // Persister la suppression dans le backend (en mettant à jour le climatiseur concerné)
            const updatedProduct = listC.find(c => c.id == climatiseurId);
            if (updatedProduct) {
                const newClim = {
                    ...updatedProduct,
                    commantaires: updatedProduct.commantaires.filter(c => c.id != commentaireId)
                };
                dispatch(updateClimatisseur(newClim));
            }
        }
    };

    const handleValider = (climatiseurId, commentaireId, currentStatus) => {
        const nextStatus = currentStatus === 'validé' ? 'en attente' : 'validé';
        dispatch(ModifierStatutCommentaire({ climatiseurId, commentaireId, status: nextStatus }));

        // Persister le changement de statut dans le backend
        const updatedProduct = listC.find(c => c.id == climatiseurId);
        if (updatedProduct) {
            const newComments = updatedProduct.commantaires.map(com => {
                if (com.id === commentaireId) {
                    return { ...com, status: nextStatus };
                }
                return com;
            });
            const newClim = { ...updatedProduct, commantaires: newComments };
            dispatch(updateClimatisseur(newClim));
        }
    };

    const handleAddComment = (e) => {
        e.preventDefault();
        const cid = parseInt(newAdminComment.climatiseurId);
        if (!cid) return alert("Veuillez choisir un produit.");
        
        const starMap = {
            '⭐⭐⭐⭐⭐': '5 étoiles',
            '⭐⭐⭐⭐': '4 étoiles',
            '⭐⭐⭐': '3 étoiles',
            '⭐⭐': '2 étoiles',
            '⭐': '1 étoile'
        };

        const newCom = {
            username: newAdminComment.username,
            avis: starMap[newAdminComment.avis] || newAdminComment.avis,
            commantaire: newAdminComment.commantaire,
            status: 'validé' // Par défaut validé pour un admin
        };

        dispatch(AjouterCommentaireAdmin({
            climatiseurId: cid,
            newComment: newCom
        }));

        // Persister l'ajout
        const updatedProduct = listC.find(c => c.id == cid);
        if (updatedProduct) {
            const allComments = listC.flatMap(c => c.commantaires || []);
            const maxId = allComments.length > 0 ? Math.max(...allComments.map(c => c.id)) : 0;
            const newClim = {
                ...updatedProduct,
                commantaires: [
                    ...updatedProduct.commantaires,
                    { ...newCom, id: maxId + 1, like: 0, dislike: 0 }
                ]
            };
            dispatch(updateClimatisseur(newClim));
        }

        setNewAdminComment({ climatiseurId: '', username: 'Admin Jamas', avis: '⭐⭐⭐⭐⭐', commantaire: '' });
        setIsAddModalOpen(false);
    };

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Modération des Commentaires</h1>
                        <p>Gérez les retours et avis de vos clients ({filteredComments.length} affichés).</p>
                    </div>
                </header>

                <div className="admin-table-container" style={{ marginTop: '20px' }}>
                    <div className="table-header-actions" style={{ flexWrap: 'wrap', gap: '15px' }}>
                        <div className="admin-filters">
                            <select 
                                className="admin-select" 
                                value={filterProduct} 
                                onChange={e => setFilterProduct(e.target.value)}
                            >
                                <option>Tous les produits</option>
                                {listC.map(c => <option key={c.id} value={c.nomClimatisseur}>{c.nomClimatisseur}</option>)}
                            </select>
                        </div>
                        <button className="admin-btn-add" onClick={() => setIsAddModalOpen(true)}>
                            <Plus size={20} />
                            <span>Répondre / Ajouter</span>
                        </button>
                    </div>

                    <div className="comments-grid" style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
                        {filteredComments.length > 0 ? (
                            filteredComments.map((com) => (
                                <div 
                                    key={`${com.climatiseurId}-${com.id}`} 
                                    className={`comment-admin-card ${highlightId === com.id ? 'highlighted' : ''}`}
                                    id={`comment-${com.id}`}
                                    style={{
                                        background: 'white',
                                        padding: '24px',
                                        borderRadius: '20px',
                                        border: highlightId === com.id ? '2px solid var(--admin-secondary)' : '1px solid var(--admin-border)',
                                        boxShadow: highlightId === com.id ? '0 0 30px rgba(112, 0, 0, 0.15)' : 'var(--admin-shadow)',
                                        transition: 'all 0.3s ease',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                            <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-primary)', border: '1px solid var(--admin-border)' }}>
                                                <User size={22} />
                                            </div>
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800' }}>{com.username}</h4>
                                                    <span style={{ 
                                                        fontSize: '0.65rem', 
                                                        fontWeight: '900', 
                                                        padding: '3px 8px', 
                                                        borderRadius: '6px',
                                                        background: com.status === 'validé' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                                        color: com.status === 'validé' ? '#166534' : '#9a3412',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {com.status === 'validé' ? 'Validé' : 'En attente'}
                                                    </span>
                                                </div>
                                                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                                                    <ShoppingCart size={10} style={{ verticalAlign: 'middle', marginRight: '4px' }}/>
                                                    {com.climatiseurNom}
                                                </p>
                                            </div>
                                        </div>
                                        <span style={{ fontSize: '0.9rem', color: 'var(--admin-secondary)', fontWeight: '800', background: 'rgba(112, 0, 0, 0.05)', padding: '5px 12px', borderRadius: '10px' }}>
                                            {com.avis}
                                        </span>
                                    </div>
                                    
                                    <p style={{ margin: '0 0 20px 0', fontSize: '0.95rem', color: 'var(--admin-text-main)', lineHeight: '1.7', padding: '0 5px' }}>
                                        {com.commantaire}
                                    </p>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid #f8fafc' }}>
                                        <div style={{ display: 'flex', gap: '20px', color: 'var(--admin-text-muted)', fontSize: '0.8rem' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Heart size={14} fill={com.like > 0 ? "var(--admin-secondary)" : "none"} color="var(--admin-secondary)" /> {com.like} Likes</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MessageCircle size={14} /> ID: #{com.id}</span>
                                        </div>
                                        <div className="action-btns">
                                            <button 
                                                className={`btn-icon ${com.status === 'validé' ? 'active-success' : ''}`} 
                                                title={com.status === 'validé' ? "Masquer" : "Valider"} 
                                                onClick={() => handleValider(com.climatiseurId, com.id, com.status)}
                                                style={{ color: com.status === 'validé' ? 'var(--admin-success)' : 'var(--admin-text-muted)' }}
                                            >
                                                <CheckCircle size={20} />
                                            </button>
                                            <button 
                                                className="btn-icon btn-delete" 
                                                title="Supprimer" 
                                                onClick={() => handleSupprimer(com.climatiseurId, com.id)}
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--admin-text-muted)', background: 'white', borderRadius: '20px', border: '1px dashed var(--admin-border)' }}>
                                <MessageSquare size={48} style={{ opacity: 0.1, marginBottom: '20px' }} />
                                <p style={{ fontWeight: '600' }}>Aucun commentaire trouvé pour cette sélection.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Add Comment Modal (Admin Reply) */}
                {isAddModalOpen && (
                    <div className="admin-modal-overlay" onClick={() => setIsAddModalOpen(false)}>
                        <div className="admin-modal-content add-comment-modal" style={{ maxWidth: '650px' }} onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><MessageSquare /> Nouveau commentaire</h2>
                                <button className="modal-close-btn" onClick={() => setIsAddModalOpen(false)}>&times;</button>
                            </div>
                            <form className="modal-body" onSubmit={handleAddComment}>
                                <div className="form-group">
                                    <label>Produit concerné</label>
                                    <select 
                                        className="admin-select" 
                                        value={newAdminComment.climatiseurId}
                                        onChange={e => setNewAdminComment({...newAdminComment, climatiseurId: e.target.value})}
                                        required
                                    >
                                        <option value="">Choisir un climatiseur...</option>
                                        {listC.map(c => <option key={c.id} value={c.id}>{c.nomClimatisseur}</option>)}
                                    </select>
                                </div>

                                <div className="form-group-row" style={{ marginTop: '5px' }}>
                                    <div className="form-group">
                                        <label>Nom de l'auteur</label>
                                        <input type="text" value={newAdminComment.username} readOnly required style={{ background: '#f8fafc', cursor: 'not-allowed', color: 'var(--admin-text-muted)' }} />
                                    </div>
                                    <div className="form-group">
                                        <label>Note / Avis</label>
                                        <select className="admin-select" value={newAdminComment.avis} onChange={e => setNewAdminComment({...newAdminComment, avis: e.target.value})}>
                                            <option value="5">⭐⭐⭐⭐⭐ (Excellent)</option>
                                            <option value="4">⭐⭐⭐⭐ (Très bien)</option>
                                            <option value="3">⭐⭐⭐ (Bien)</option>
                                            <option value="2">⭐⭐ (Passable)</option>
                                            <option value="1">⭐ (Mauvais)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Message</label>
                                    <textarea 
                                        rows="4" 
                                        placeholder="Saisissez votre réponse ou avis..." 
                                        value={newAdminComment.commantaire} 
                                        onChange={e => setNewAdminComment({...newAdminComment, commantaire: e.target.value})} 
                                        required
                                    />
                                </div>
                                <button type="submit" className="admin-btn-save" style={{ width: '100%', justifyContent: 'center' }}>
                                    Publier le commentaire
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
